import { useParams } from 'react-router-dom';
import { useSetupCampaigns } from '@state/useSetupCampaigns';
import { routeSlugOf } from '@pages/flight-deck/analytics';
import { resolveTenant } from '../tenants/index';
import type { Campaign } from '@pages/setup/types';

/**
 * Canonical "what campaign is this page for?" resolver used by every
 * program-scoped page. Resolution order:
 *   1. URL `:program` segment IF it resolves to an active campaign
 *      (e.g. /25a1c/clinical — guards against stale /fobt/ bookmarks
 *      that don't match any current folder slug)
 *   2. Store-pinned `selectedCampaignId` → its `routeSlugOf`
 *   3. First active+non-archived campaign
 *   4. Last-ditch: the current tenant's own first program (resolveTenant().
 *      programs[0]); '25fobt' only if that is somehow absent
 *
 * Pre-2026-05-25 the pages each had `?? 'fobt'` and would silently render
 * FOBT data on a URL-less direct visit. Replaced with this hook so the
 * campaign segregation William explicitly required actually holds.
 */
export function useCurrentProgramSlug(): string {
  const { program } = useParams<{ program?: string }>();
  const campaigns = useSetupCampaigns((s) => s.campaigns);
  const selectedCampaignId = useSetupCampaigns((s) => s.selectedCampaignId);
  // Only trust the URL slug when it maps to an active campaign — stops a
  // stale /fobt/* bookmark from showing FOBT-empty state on an A1C session.
  if (program && program.trim()) {
    const validMatch = campaigns.find(
      (c) => c.active && !c.archived && routeSlugOf(c) === program,
    );
    if (validMatch) return program;
  }
  const selected = campaigns.find((c) => c.id === selectedCampaignId);
  if (selected) return routeSlugOf(selected);
  const firstActive = campaigns.find((c) => c.active && !c.archived);
  if (firstActive) return routeSlugOf(firstActive);
  // Last-ditch (campaigns not yet loaded on a cold first paint): fall back to
  // the CURRENT tenant's own first program, not a hardcoded cross-tenant
  // '25fobt'. Otherwise a non-Alignment tenant briefly generates /25fobt/* nav
  // links / page routes before the campaign list arrives, which then render
  // NO DATASET. Alignment's programs[0] is still '25fobt', so it is unchanged.
  return resolveTenant().programs?.[0] ?? '25fobt';
}

/**
 * The Campaign object for the current program context (same resolution as
 * useCurrentProgramSlug). Undefined when no campaign matches — e.g. fixtures
 * before hydrate, or a slug with no active campaign. Used for per-campaign
 * concerns like hidden-page visibility (Campaign.hiddenPages).
 */
export function useCurrentCampaign(): Campaign | undefined {
  const slug = useCurrentProgramSlug();
  const campaigns = useSetupCampaigns((s) => s.campaigns);
  return campaigns.find((c) => routeSlugOf(c) === slug);
}
